import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2,
  RefreshCw,
  Copy,
  CheckCircle2,
  Send,
  FileText,
  Activity,
  Ban,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/time";

const DUTCHIE_INTAKE_URL = "https://dutchie.webflow.io/request-api-key#demo";
const ONBOARDING_URL = "https://budlink.linklitepro.com/onboarding";

// Generic, audience-agnostic version of the outreach email — used by the
// "Bulk send" panel at the top of the tab so William can BCC every
// dispensary admin at once instead of clicking Copy 16 times. No name
// substitution; reads naturally when sent to multiple recipients.
const buildBulkEmail = () => {
  const subject = "Quick API approval for your dispensary on BudLink (2-min form)";
  const body = `Hi there,

I'm William, founder of BudLink (Link-Lite Data LLC) — the Pennsylvania-only medical-cannabis search engine patients use to find the exact strain, vape, or concentrate they want at a licensed dispensary near them. Your dispensary is already listed in our directory, and patients searching your area see your storefront — but your shelf shows up empty because we don't have your live menu feed yet.

Good news: Dutchie's Partner Support team has pre-cleared BudLink for Partner API access (support ticket #667778). Your team just needs to submit one short form on Dutchie's end — about 2 minutes — and your live menu starts flowing into BudLink automatically. Dutchie typically issues the API key within 3 business days.

One note from Dutchie: the form must be submitted by your dispensary's authorized Dutchie account administrator (usually the GM or owner — the person Dutchie has on file as the admin). If that isn't you, please forward this email to them.

Everything you need — plus the form link — is on this single page I built for dispensaries:

→ ${ONBOARDING_URL}

What you get:
  • Free, permanent listing of your live menu to every PA patient searching your area
  • Direct order-page deep links — we drive traffic to YOU, we don't take orders
  • Sales & promotions surfacing — daily deals and featured inventory get pushed to nearby patients in real time
  • Zero ongoing work — sync runs automatically through Dutchie's official Partner API
  • Featured-placement and analytics add-ons available if you ever want premium, but the basic listing stays free

Patients are searching "Who has this strain near me?" and "Where's the best deal today?" right now. Approving the API takes 5 minutes of admin time and your menu shows up next to every relevant search the same week.

Reply if you have any questions — happy to jump on a 5-minute call to walk your admin through the form.

Thanks,
William Stoudt
Founder, BudLink (Link-Lite Data LLC)
wstoudt@linklitepro.com · https://budlink.linklitepro.com`;
  return { subject, body };
};

// Trulieve PA stores are ALREADY live on BudLink via Trulieve's public
// Partner API — no Dutchie intake needed. Sell them on premium activation
// instead: featured placement, sales-banner surfacing, free for 30 days.
const buildTrulieveEmail = ({ name, city }) => {
  const subject = `${name || "Your Trulieve PA store"} is already live on BudLink — claim your 30-day premium`;
  const body = `Hi there,

I'm William, founder of BudLink (Link-Lite Data LLC) — the Pennsylvania-only medical-cannabis search engine patients use to find the right product at the right price near them.

Here's the patient reality I built BudLink around: insurance doesn't cover medical cannabis. Every gram, every cart, every tincture comes out of the patient's pocket — and the same strain can cost 30–40% more at one shop than at another a few miles away. BudLink lets patients compare every PA dispensary's live menu side-by-side and shop where it makes financial sense for them.

Quick heads-up: ${name || "your Trulieve PA store"} is ALREADY live on BudLink. Your full real-time menu is syncing through Trulieve's Partner API and PA patients searching ${city || "your area"} see your shelf next to every relevant query — strain, brand, price, THC%, in-stock status, all of it.

That part costs you nothing — permanently free.

Here's why I'm writing: I'd like to offer you 30 days of BudLink Premium, free, no card on file. Premium activates three things on your listing:

  1. Featured placement — your store ranks above standard listings in BudLink search results within your trade area. Clearly labeled as "Featured" so it stays compliant with PA advertising guidelines.
  2. Promo & sale callouts — when a product is on sale on your Trulieve checkout, BudLink surfaces it as a "Deal" badge in nearby search results in real time. Most dispensaries lose deal-driven traffic because patients don't know what's on sale until they walk in.
  3. Listing analytics — how many patients viewed your menu, which products clicked through to ordering, which promos converted. The data your shop should have but the POS doesn't give you.

The patients who use BudLink are price-conscious by definition — they're shopping around because they have to. Premium puts your menu in front of them first, and your sales in front of them in real time. When dispensaries compete, patients save money — and the dispensary that competes hardest builds the most loyal patient base.

After 30 days, Premium converts to a monthly subscription if you want to continue. No auto-charge — I'll send you a usage report and you decide. Cancel any time with one email.

Reply yes and I'll activate Premium on your store within an hour.

Thanks,
William Stoudt
Founder, BudLink (Link-Lite Data LLC)
wstoudt@linklitepro.com · https://budlink.linklitepro.com`;
  return { subject, body };
};

// Single, action-oriented outreach email. Per user direction — no
// follow-up sequence, no Resend automation. Copy-paste only.
const buildEmail = ({ name, city, manager_name, pos_hint }) => {
  // Trulieve PA stores get a totally different pitch — they're already
  // live via Trulieve's Partner API, so the ask is premium activation,
  // not Dutchie API approval.
  if (pos_hint === "trulieve" || (name || "").toLowerCase().includes("trulieve")) {
    const { subject, body } = buildTrulieveEmail({ name, city });
    const greet = manager_name ? body.replace(/^Hi there,/, `Hi ${manager_name},`) : body;
    return { subject, body: greet };
  }
  const greet = manager_name ? `Hi ${manager_name},` : "Hi there,";
  const subject = `Quick API approval for ${name} on BudLink (2-min form)`;
  const body = `${greet}

I'm William, founder of BudLink (Link-Lite Data LLC) — the Pennsylvania-only medical-cannabis search engine patients use to find the exact strain, vape, or concentrate they want at a licensed dispensary near them. ${name} is already listed in our directory, and patients searching ${city || "your area"} see your storefront — but your shelf shows up empty because we don't have your live menu feed yet.

Good news: Dutchie's Partner Support team has pre-cleared BudLink for Partner API access (support ticket #667778). Your team just needs to submit one short form on Dutchie's end — about 2 minutes — and your live menu starts flowing into BudLink automatically. Dutchie typically issues the API key within 3 business days.

One note from Dutchie: the form must be submitted by ${name}'s authorized Dutchie account administrator (usually the GM or owner — the person Dutchie has on file as the admin). If that isn't you, please forward this email to them.

Everything you need — plus the form link — is on this single page I built for dispensaries:

→ ${ONBOARDING_URL}

What you get:
  • Free, permanent listing of your live menu to every PA patient searching your area
  • Direct order-page deep links — we drive traffic to YOU, we don't take orders
  • Sales & promotions surfacing — daily deals and featured inventory get pushed to nearby patients in real time
  • Zero ongoing work — sync runs automatically through Dutchie's official Partner API
  • Featured-placement and analytics add-ons available if you ever want premium, but the basic listing stays free

Patients are searching "Who has this strain near me?" and "Where's the best deal today?" right now. Approving the API takes 5 minutes of admin time and your menu shows up next to every relevant search the same week.

Reply if you have any questions — happy to jump on a 5-minute call to walk your admin through the form.

Thanks,
William Stoudt
Founder, BudLink (Link-Lite Data LLC)
wstoudt@linklitepro.com · https://budlink.linklitepro.com`;
  return { subject, body };
};

const STATUS_META = {
  not_contacted: { label: "Not contacted", icon: Clock, tone: "text-foreground/50 border-border" },
  emailed: { label: "Emailed", icon: Send, tone: "text-amber-200 border-amber-500/40 bg-amber-500/10" },
  form_submitted: { label: "Form submitted", icon: FileText, tone: "text-sky-200 border-sky-500/40 bg-sky-500/10" },
  api_live: { label: "API live", icon: CheckCircle2, tone: "text-emerald-200 border-emerald-500/40 bg-emerald-500/10" },
  declined: { label: "Declined", icon: Ban, tone: "text-rose-200 border-rose-500/40 bg-rose-500/10" },
};
const STATUS_ORDER = ["not_contacted", "emailed", "form_submitted", "api_live", "declined"];

export const OutreachTab = () => {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/outreach/dutchie");
      setRows(r.data.rows || []);
      setCounts(r.data.by_status || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.outreach.status !== filter) return false;
      if (!q) return true;
      const hay = `${r.name} ${r.city} ${r.outreach.manager_name} ${r.outreach.manager_email}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, filter, search]);

  if (rows === null) {
    return (
      <div className="border border-border bg-card rounded-2xl p-10 text-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <section data-testid="outreach-tab" className="space-y-5">
      <InboundRequests />
      <header className="border border-border bg-card rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow flex items-center gap-2 mb-1">
              <Activity className="w-3.5 h-3.5 text-emerald-300" />
              Dutchie partner-API approval pipeline
            </div>
            <h2 className="font-serif text-2xl">
              {rows.length} Dutchie-routed dispensar{rows.length === 1 ? "y" : "ies"}
            </h2>
            <p className="text-xs text-foreground/60 mt-2 max-w-xl leading-relaxed">
              Dutchie Support (ticket #667778) has pre-cleared Bud Link for
              Partner API access — each dispensary admin just needs to submit{" "}
              <a
                href={DUTCHIE_INTAKE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-emerald-200 hover:text-emerald-100"
              >
                this 2-minute intake form
              </a>
              . Track every chain&apos;s status below.
            </p>
          </div>
          <Button
            data-testid="outreach-refresh"
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="rounded-full"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-5">
          {STATUS_ORDER.map((s) => {
            const meta = STATUS_META[s];
            const active = filter === s;
            const Icon = meta.icon;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(active ? "all" : s)}
                data-testid={`outreach-filter-${s}`}
                className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                  active
                    ? `${meta.tone} ring-1 ring-emerald-400/40`
                    : "border-border bg-background hover:border-foreground/30"
                }`}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-foreground/60">
                  <Icon className="w-3 h-3" />
                  {meta.label}
                </div>
                <div className="font-serif text-2xl mt-1">{counts[s] || 0}</div>
              </button>
            );
          })}
        </div>
      </header>

      <BulkSendPanel rows={rows} />

      <div className="flex items-center gap-2">
        <Input
          data-testid="outreach-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by dispensary, city, manager…"
          className="bg-card border-border h-10 max-w-md"
        />
        {filter !== "all" && (
          <button
            onClick={() => setFilter("all")}
            className="text-xs text-foreground/60 hover:text-foreground underline"
          >
            Clear filter
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-10 text-center text-muted-foreground text-sm">
          No dispensaries match.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <OutreachRow key={r.slug} row={r} onChange={load} />
          ))}
        </div>
      )}
    </section>
  );
};

const OutreachRow = ({ row, onChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [managerName, setManagerName] = useState(row.outreach.manager_name || "");
  const [managerEmail, setManagerEmail] = useState(row.outreach.manager_email || "");
  const [managerPhone, setManagerPhone] = useState(row.outreach.manager_phone || "");
  const [notes, setNotes] = useState(row.outreach.notes || "");
  const [saving, setSaving] = useState(false);

  const meta = STATUS_META[row.outreach.status] || STATUS_META.not_contacted;
  const Icon = meta.icon;

  const patch = async (payload) => {
    setSaving(true);
    try {
      await api.patch(`/admin/outreach/${row.slug}`, payload);
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveContact = () =>
    patch({
      manager_name: managerName,
      manager_email: managerEmail,
      manager_phone: managerPhone,
      notes,
    });

  const setStatus = (status) => patch({ status });

  const copyEmail = async () => {
    const { subject, body } = buildEmail({
      name: row.name,
      city: row.city,
      manager_name: managerName,
      pos_hint: row.pos_hint || row.pos_detected,
    });
    const payload = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(payload);
      toast.success("Email copied — paste into Gmail / Outlook", {
        description: "Subject is the first line; everything below is the body.",
        duration: 7000,
      });
    } catch (e) {
      toast.error("Clipboard blocked — select the text in the preview below");
      setExpanded(true);
    }
  };

  const mailtoHref = (() => {
    const { subject, body } = buildEmail({
      name: row.name,
      city: row.city,
      manager_name: managerName,
      pos_hint: row.pos_hint || row.pos_detected,
    });
    const to = managerEmail || "";
    return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  })();

  const openIntake = () => window.open(DUTCHIE_INTAKE_URL, "_blank", "noopener");

  return (
    <div
      data-testid={`outreach-row-${row.slug}`}
      className="border border-border bg-card rounded-2xl overflow-hidden"
    >
      <div className="p-4 flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-serif text-lg">{row.name}</div>
            <span
              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.tone}`}
            >
              <Icon className="w-3 h-3" />
              {meta.label}
            </span>
          </div>
          <div className="text-xs text-foreground/50 mt-1">
            {row.city}, {row.state} · {row.address}
            {row.dutchie_slug && (
              <span className="ml-2 px-1.5 py-0.5 rounded bg-background border border-border font-mono text-[10px]">
                dutchie/{row.dutchie_slug}
              </span>
            )}
          </div>
          {row.outreach.last_contacted_at && (
            <div className="text-[11px] text-foreground/40 mt-1">
              Last contacted {timeAgo(row.outreach.last_contacted_at)}
              {row.outreach.form_submitted_at &&
                ` · form submitted ${timeAgo(row.outreach.form_submitted_at)}`}
              {row.outreach.api_live_at &&
                ` · API live ${timeAgo(row.outreach.api_live_at)}`}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid={`outreach-copy-${row.slug}`}
            variant="outline"
            size="sm"
            onClick={copyEmail}
            disabled={saving}
            className="rounded-full h-9 px-3 text-xs"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy email
          </Button>
          <a
            data-testid={`outreach-mailto-${row.slug}`}
            href={managerEmail ? mailtoHref : undefined}
            onClick={(e) => {
              if (!managerEmail) {
                e.preventDefault();
                toast.error("Add the manager's email first (Details → Manager email).");
              }
            }}
            aria-disabled={!managerEmail}
            title={managerEmail ? "Open this email in your mail client" : "Add a manager email first"}
            className={`inline-flex items-center rounded-full h-9 px-3 text-xs border ${
              managerEmail
                ? "border-border bg-background hover:bg-card cursor-pointer"
                : "border-border bg-background opacity-50 cursor-not-allowed"
            }`}
          >
            <Send className="w-3.5 h-3.5 mr-1.5" /> Open in Mail
          </a>
          <Button
            data-testid={`outreach-intake-${row.slug}`}
            variant="outline"
            size="sm"
            onClick={openIntake}
            className="rounded-full h-9 px-3 text-xs"
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Intake form
          </Button>
          <Button
            data-testid={`outreach-expand-${row.slug}`}
            size="sm"
            onClick={() => setExpanded((x) => !x)}
            className="rounded-full h-9 px-3 text-xs bg-foreground text-background hover:bg-foreground/90"
          >
            {expanded ? "Close" : "Details"}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 space-y-4 bg-background/40">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-foreground/50">
                Manager name
              </label>
              <Input
                data-testid={`outreach-name-${row.slug}`}
                value={managerName}
                onChange={(e) => setManagerName(e.target.value)}
                className="bg-card border-border h-9 mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-foreground/50">
                Manager email
              </label>
              <Input
                data-testid={`outreach-email-${row.slug}`}
                type="email"
                value={managerEmail}
                onChange={(e) => setManagerEmail(e.target.value)}
                className="bg-card border-border h-9 mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-foreground/50">
                Manager phone
              </label>
              <Input
                data-testid={`outreach-phone-${row.slug}`}
                value={managerPhone}
                onChange={(e) => setManagerPhone(e.target.value)}
                className="bg-card border-border h-9 mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-foreground/50">
              Notes
            </label>
            <Textarea
              data-testid={`outreach-notes-${row.slug}`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. spoke with assistant manager, callback Tuesday"
              className="bg-card border-border mt-1 text-sm"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              data-testid={`outreach-save-${row.slug}`}
              variant="outline"
              size="sm"
              onClick={saveContact}
              disabled={saving}
              className="rounded-full h-9 px-4 text-xs"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Save contact
            </Button>
            <span className="text-[10px] uppercase tracking-wider text-foreground/40 mx-2">
              Mark as
            </span>
            {STATUS_ORDER.map((s) => {
              const sm = STATUS_META[s];
              const active = row.outreach.status === s;
              return (
                <button
                  key={s}
                  data-testid={`outreach-status-${s}-${row.slug}`}
                  onClick={() => setStatus(s)}
                  disabled={saving || active}
                  className={`text-[11px] px-3 h-9 rounded-full border transition ${
                    active
                      ? `${sm.tone} cursor-default`
                      : "border-border bg-background hover:border-foreground/40 text-foreground/70"
                  }`}
                >
                  {sm.label}
                </button>
              );
            })}
          </div>
          <details className="text-xs text-foreground/60">
            <summary className="cursor-pointer text-foreground/80">
              Email preview
            </summary>
            <pre
              data-testid={`outreach-preview-${row.slug}`}
              className="mt-2 whitespace-pre-wrap bg-background border border-border rounded-xl p-3 leading-relaxed"
            >
              {(() => {
                const { subject, body } = buildEmail({
                  name: row.name,
                  city: row.city,
                  manager_name: managerName,
                  pos_hint: row.pos_hint || row.pos_detected,
                });
                return `Subject: ${subject}\n\n${body}`;
              })()}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};


const BulkSendPanel = ({ rows }) => {
  const [showFull, setShowFull] = React.useState(false);
  const { subject, body } = React.useMemo(() => buildBulkEmail(), []);
  const fullText = `Subject: ${subject}\n\n${body}`;

  // Comma-separated BCC list of every manager email saved across all rows.
  const bccList = React.useMemo(() => {
    const seen = new Set();
    (rows || []).forEach((r) => {
      const e = (r?.outreach?.manager_email || "").trim();
      if (e && !seen.has(e.toLowerCase())) seen.add(e.toLowerCase());
    });
    return Array.from(seen).join(", ");
  }, [rows]);

  const bccCount = bccList ? bccList.split(",").length : 0;
  // Total dispensaries vs how many we have an email for, so the user knows
  // exactly how much coverage the BCC list represents before sending.
  const totalRows = (rows || []).length;

  const mailtoHref = `mailto:?bcc=${encodeURIComponent(
    bccList
  )}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`, { duration: 5000 });
    } catch {
      toast.error("Clipboard blocked — select the text below and copy manually.");
      setShowFull(true);
    }
  };

  return (
    <section
      data-testid="bulk-send-panel"
      className="border border-emerald-500/30 bg-emerald-500/5 rounded-2xl p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="eyebrow text-emerald-200 mb-1">Bulk send</div>
          <h3 className="font-serif text-xl">One email to every dispensary</h3>
          <p className="text-xs text-foreground/60 mt-1.5 max-w-xl leading-relaxed">
            One copy, paste once, BCC every manager. The same template — but
            written generically so it reads naturally going to all dispensaries at once.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button
          data-testid="bulk-copy-email"
          onClick={() => copy(fullText, "Email (subject + body)")}
          className="rounded-full h-10 px-5 bg-emerald-500 text-emerald-950 hover:bg-emerald-400 text-xs font-semibold"
        >
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy email (subject + body)
        </Button>
        <Button
          data-testid="bulk-copy-bcc"
          variant="outline"
          onClick={() => copy(bccList, `${bccCount} manager email${bccCount === 1 ? "" : "s"}`)}
          disabled={!bccList}
          title={bccList ? "Paste into Gmail BCC field" : "No manager emails saved yet — open Details on a row and add one"}
          className="rounded-full h-10 px-4 text-xs"
        >
          <Copy className="w-3.5 h-3.5 mr-1.5" />
          {bccList ? `Copy BCC list (${bccCount}/${totalRows})` : "No manager emails saved yet"}
        </Button>
        {bccList && (
          <a
            data-testid="bulk-mailto"
            href={mailtoHref}
            className="inline-flex items-center rounded-full h-10 px-4 text-xs border border-border bg-background hover:bg-card"
            title="Open a new Mail draft with everyone in BCC + the email pre-filled"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" /> Open Mail draft (all BCC&apos;d)
          </a>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowFull((x) => !x)}
          className="rounded-full h-10 px-4 text-xs text-foreground/70 hover:text-foreground"
        >
          {showFull ? "Hide email" : "Preview email"}
        </Button>
      </div>

      {!bccList && (
        <div className="text-[11px] text-amber-200/80 mb-3 leading-relaxed">
          Tip: open <span className="font-medium">Details</span> on each dispensary
          and paste the manager email — once you&apos;ve filled in at least one,
          the BCC list activates.
        </div>
      )}

      {showFull && (
        <pre
          data-testid="bulk-email-preview"
          className="whitespace-pre-wrap bg-background border border-border rounded-xl p-4 text-xs leading-relaxed mt-2 select-all"
        >
          {fullText}
        </pre>
      )}
    </section>
  );
};

// "Ask your dispensary to join" inbound requests — submitted by patients
// via AskDispensaryDialog. Lives at the top of OutreachTab so the operator
// sees demand signal before working the cold-outreach pipeline.
const InboundRequests = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/dispensary-requests");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (data === null) return null;
  const items = data.items || [];
  const top = data.top_requested || [];

  return (
    <section
      data-testid="inbound-requests"
      className="border border-emerald-500/30 bg-emerald-500/5 rounded-2xl p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="eyebrow flex items-center gap-2 mb-1 text-emerald-300">
            <Activity className="w-3.5 h-3.5" />
            Patient demand · inbound requests
          </div>
          <h2 className="font-serif text-2xl">
            {data.total ?? 0} request{(data.total ?? 0) === 1 ? "" : "s"}
            {data.new > 0 && (
              <span className="ml-2 text-emerald-300 text-base">
                · {data.new} new
              </span>
            )}
          </h2>
          <p className="text-xs text-foreground/60 mt-2 max-w-xl leading-relaxed">
            Patients submitted these via the &ldquo;Ask your dispensary to
            join&rdquo; flow. Most-asked shops bubble up first so you know
            where the demand is.
          </p>
        </div>
        <Button
          data-testid="inbound-requests-refresh"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="rounded-full"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-foreground/60 py-6 text-center">
          No inbound requests yet.
        </div>
      ) : (
        <>
          {top.length > 0 && (
            <div className="mb-4">
              <div className="eyebrow mb-2">Most-requested shops</div>
              <div className="flex flex-wrap gap-2">
                {top.map((t) => (
                  <span
                    key={t.name}
                    data-testid={`inbound-top-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-background border border-border px-3 h-7 text-xs"
                  >
                    {t.name}
                    <span className="text-emerald-300 font-mono">{t.votes}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="border-t border-border/40 pt-3 space-y-2">
            <div className="eyebrow mb-2">Recent submissions</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-foreground/50">
                  <tr className="text-left">
                    <th className="py-1.5 pr-3 font-normal">Shop</th>
                    <th className="py-1.5 pr-3 font-normal">ZIP</th>
                    <th className="py-1.5 pr-3 font-normal">Email</th>
                    <th className="py-1.5 pr-3 font-normal">Note</th>
                    <th className="py-1.5 pr-3 font-normal">When</th>
                  </tr>
                </thead>
                <tbody className="text-foreground/80">
                  {items.slice(0, 20).map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`inbound-row-${r.id}`}
                      className="border-t border-border/30"
                    >
                      <td className="py-2 pr-3 font-medium">{r.dispensary_name}</td>
                      <td className="py-2 pr-3 font-mono text-foreground/60">{r.zip || "—"}</td>
                      <td className="py-2 pr-3 text-foreground/60">{r.email || "—"}</td>
                      <td className="py-2 pr-3 text-foreground/60 max-w-[20ch] truncate">
                        {r.note || "—"}
                      </td>
                      <td className="py-2 pr-3 text-foreground/50 whitespace-nowrap">
                        {timeAgo(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

